<template>
    <div
        :id="`quote-${quote.id}`"
        class="foxy-card !rounded-2xl p-6 !duration-300 flex flex-col justify-between"
        :class="{ 'notification-target': isNotificationTarget, 'h-full': presentationMode === 'full' }">
        <!-- User Info -->
        <div>
            <div class="flex items-center space-x-3 justify-between">
                <div class="flex items-center space-x-3">
                    <template v-if="quote.user && quote.user.username">
                        <a :href="profileUrl(quote.user.username)"
                           class="flex items-center space-x-3 group"
                           @click.stop>
                            <user-avatar
                                :src="quote.user.avatar_display || ''"
                                :name="quote.user.name"
                                size="sm"
                                class="bg-gradient-to-br from-blue-500 to-green-500 text-sm text-white transition-transform duration-200 group-hover:scale-110"
                            />
                            <div>
                                <div class="text-sm font-medium text-foxy-text group-hover:text-foxy-action transition-colors">
                                    {{ quote.user?.name || 'Користувач' }}
                                </div>
                                <div v-if="quote.user?.username"
                                    class="text-xs text-foxy-text-secondary group-hover:text-foxy-action transition-colors">
                                    {{ '@' + quote.user.username }}
                                </div>
                                <div class="text-xs text-foxy-text-secondary">
                                    {{ formatDate(quote.published_at || quote.created_at) }}
                                </div>
                            </div>
                        </a>
                    </template>
                    <template v-else>
                        <user-avatar
                            :src="quote.user && quote.user.avatar_display ? quote.user.avatar_display : ''"
                            :name="quote.user ? quote.user.name : ''"
                            size="sm"
                            class="bg-gradient-to-br from-blue-500 to-green-500 text-sm text-white"
                        />
                        <div>
                            <div class="text-sm font-medium text-foxy-text">
                                {{ quote.user?.name || 'Користувач' }}
                            </div>
                            <div class="text-xs text-foxy-text-secondary">
                                {{ formatDate(quote.published_at || quote.created_at) }}
                            </div>
                        </div>
                    </template>
                </div>
                <!-- Quote Icon -->
                <div class="mb-4">
                    <svg width="32" height="14" viewBox="0 0 32 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M6.9682 0.131836C8.63086 0.131836 10.0929 0.533169 11.3542 1.33584C12.6729 2.08117 13.4755 3.05584 13.7622 4.25983C14.3355 6.38117 13.9342 8.44517 12.5582 10.4518C11.2395 12.4012 9.4622 13.2898 7.2262 13.1178C8.71686 12.2578 9.3762 11.1972 9.2042 9.93583C8.45886 10.1078 7.71353 10.1938 6.9682 10.1938C5.01886 10.1938 3.3562 9.7065 1.9802 8.73183C0.661531 7.75717 0.00219727 6.58184 0.00219727 5.20584C0.00219727 3.7725 0.661531 2.5685 1.9802 1.59383C3.3562 0.619168 5.01886 0.131836 6.9682 0.131836ZM30.8762 4.25983C31.4495 6.38117 31.0769 8.44517 29.7582 10.4518C28.4395 12.4012 26.6335 13.2898 24.3402 13.1178C25.8309 12.2578 26.4902 11.1972 26.3182 9.93583C25.5729 10.1078 24.8275 10.1938 24.0822 10.1938C22.1329 10.1938 20.4702 9.7065 19.0942 8.73183C17.7755 7.75717 17.1162 6.58184 17.1162 5.20584C17.1162 3.7725 17.7755 2.5685 19.0942 1.59383C20.4702 0.619168 22.1329 0.131836 24.0822 0.131836C25.7449 0.131836 27.2069 0.533169 28.4682 1.33584C29.7869 2.08117 30.5895 3.05584 30.8762 4.25983Z"
                            fill="#F97316" />
                    </svg>
                </div>
            </div>
            <a v-if="showBookContext && quote.book?.slug && quote.book?.title" :href="`/books/${quote.book.slug}`"
                class="mt-3 inline-flex text-sm font-semibold text-foxy-action hover:underline">
                {{ quote.book.title }}
            </a>
            <!-- Quote Text -->
            <div v-if="!isEditing">
                <!-- Blocked Content -->
                <div v-if="quote.status === 'blocked'" class="my-3">
                    <div class="border-2 border-red-300 dark:border-red-700 rounded-lg bg-red-50/50 dark:bg-red-900/20 p-4">
                        <div class="relative overflow-hidden rounded-md mb-3">
                            <blockquote class="text-lg text-foxy-text leading-relaxed font-medium italic break-words blur-sm filter"
                                style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                "{{ quote.content }}"
                            </blockquote>
                        </div>
                        <div class="flex items-start gap-3 pt-2 border-t border-red-200 dark:border-red-800">
                            <div class="flex-shrink-0 mt-0.5">
                                <i class="fas fa-exclamation-triangle text-red-500 dark:text-red-400 text-lg"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs text-red-700 dark:text-red-300 font-medium mb-1">
                                    Контент заблоковано адміністрацією сайту
                                </p>
                                <p v-if="quote.moderation_reason" class="text-xs text-red-600 dark:text-red-400 mt-1">
                                    Причина: {{ quote.moderation_reason }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Normal Content -->
                <blockquote v-else ref="content" class="max-w-[72ch] whitespace-pre-line text-lg text-foxy-text leading-relaxed font-medium italic mt-3.5 sm:mt-4 mb-4 break-words"
                    :class="presentationMode === 'compact' && !isExpanded ? 'quote-clamped-compact' : ''"
                    style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">{{ quote.content }}</blockquote>
                <button v-if="quote.status !== 'blocked' && presentationMode === 'compact' && (isExpanded || isOverflowing)" type="button"
                    class="mb-3 text-sm font-semibold text-foxy-action hover:underline" :aria-expanded="String(isExpanded)" @click="toggleExpanded">
                    {{ isExpanded ? 'Згорнути' : 'Розгорнути' }}
                </button>
            </div>
            
            <!-- Edit Form -->
            <div v-else class="my-4">
                <textarea v-model="editContent" 
                    class="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    rows="3" placeholder="Введіть цитату..."></textarea>
                <input v-model="editPageNumber" type="number" min="1" placeholder="Номер сторінки (необов'язково)"
                    class="w-full mt-2 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <div class="flex items-center space-x-2 mt-2">
                    <label class="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <input v-model="editIsPublic" type="checkbox" class="mr-2">
                        Публічна цитата
                    </label>
                </div>
                <div class="flex space-x-2 mt-3">
                    <button @click="saveEdit" :disabled="isSaving"
                        class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm">
                        {{ isSaving ? 'Збереження...' : 'Зберегти' }}
                    </button>
                    <button @click="cancelEdit" :disabled="isSaving"
                        class="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 text-sm">
                        Скасувати
                    </button>
                </div>
            </div>
        </div>
        <div>

            <!-- Page Number -->
            <div v-if="quote.page_number" class="text-sm text-foxy-text-secondary mb-4">
                Сторінка {{ quote.page_number }}
            </div>
            <!-- Actions -->
            <div class="flex items-center space-x-4">
                <!-- Like Button -->
                <button @click="toggleLike"
                    class="flex items-center space-x-1 text-foxy-text-secondary hover:text-red-500 dark:hover:text-red-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm">
                    <i :class="['fas fa-heart', isLiked ? 'text-red-500 dark:text-red-400' : '']"></i>
                    <span class="text-sm">{{ likesCount }}</span>
                </button>

                <!-- Saved Button -->
                <button v-if="quote.can_favorite" @click="toggleFavorite"
                    :class="['flex items-center space-x-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm', isFavorited ? 'text-foxy-text' : 'text-foxy-text-secondary hover:text-foxy-text', isFavoritePending ? 'cursor-not-allowed opacity-60' : '']"
                    :aria-pressed="isFavorited ? 'true' : 'false'"
                    :aria-busy="isFavoritePending ? 'true' : 'false'"
                    :aria-label="isFavorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                    :title="isFavorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                    :disabled="isFavoritePending">
                    <i :class="[isFavorited ? 'fas' : 'far', 'fa-bookmark', 'text-lg']" aria-hidden="true"></i>
                </button>

                <!-- Share Button -->
                <button @click="shareQuote"
                    class="flex items-center space-x-1 text-foxy-text-secondary hover:text-foxy-action transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm">
                    <i class="fas fa-share-alt"></i>
                </button>


                <!-- Delete Button (for owner) -->
                <button v-if="canDelete" @click="deleteQuote"
                    class="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors">
                    <i class="fas fa-trash text-sm"></i>
                </button>

                <!-- Report Button -->
                <report-button 
                    :reportable-type="'App\\Models\\Quote'"
                    :reportable-id="quote.id"
                    :content-preview="getContentPreview()"
                    :content-url="getContentUrl()">
                </report-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { shareContent } from '../../utils/shareHelper';

export default {
    name: 'QuoteCard',
    props: {
        quote: {
            type: Object,
            required: true
        },
        bookSlug: {
            type: String,
            default: ''
        },
        currentUserId: {
            type: Number,
            default: null
        },
        isNotificationTarget: {
            type: Boolean,
            default: false
        },
        showBookContext: { type: Boolean, default: false },
        presentationMode: {
            type: String,
            default: 'full',
            validator: value => ['full', 'compact'].includes(value)
        }
    },
    data() {
        return {
            isLiked: this.quote.is_liked_by_current_user || false,
            likesCount: this.quote.likes_count || 0,
            isFavorited: this.quote.is_favorited_by_current_user || false,
            isFavoritePending: false,
            isExpanded: false,
            isOverflowing: false,
            isEditing: false,
            editContent: '',
            editPageNumber: null,
            editIsPublic: true,
            isSaving: false,
        };
    },
    computed: {
        canDelete() {
            return this.currentUserId && this.quote.user_id === this.currentUserId;
        },
        canEdit() {
            return this.currentUserId && this.quote.user_id === this.currentUserId;
        },
        effectiveBookSlug() {
            // Используем bookSlug из prop, если он передан, иначе используем slug из quote.book
            return this.bookSlug || (this.quote.book && this.quote.book.slug) || '';
        }
    },
    mounted() {
        this.$nextTick(this.checkOverflow);
        window.addEventListener('resize', this.checkOverflow);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkOverflow);
    },
    watch: {
        'quote.content'() { this.isExpanded = false; this.$nextTick(this.checkOverflow); },
        isEditing() { this.$nextTick(this.checkOverflow); }
    },
    methods: {
        checkOverflow() {
            if (this.presentationMode !== 'compact' || this.isExpanded) return;
            this.$nextTick(() => {
                const element = this.$refs.content;
                this.isOverflowing = Boolean(element && element.scrollHeight > element.clientHeight + 1);
            });
        },
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
            if (!this.isExpanded) this.$nextTick(this.checkOverflow);
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diff = Math.floor((now - date) / 1000);

            if (diff < 60) return 'щойно';
            if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
            if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;

            return date.toLocaleDateString('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        },
        async toggleLike() {
            if (!this.currentUserId) {
                this.$emit('show-notification', 'Будь ласка, увійдіть, щоб поставити лайк.', 'error');
                return;
            }
            try {
                const bookSlug = this.effectiveBookSlug;
                if (!bookSlug) {
                    this.$emit('show-notification', 'Помилка: не вдалося визначити книгу.', 'error');
                    return;
                }
                const response = await axios.post(`/books/${bookSlug}/quotes/${this.quote.id}/like`);
                if (response.data.success) {
                    this.isLiked = response.data.is_liked;
                    this.likesCount = response.data.likes_count;
                    this.$emit('like-toggled', { quoteId: this.quote.id, isLiked: this.isLiked, likesCount: this.likesCount });
                }
            } catch (error) {
                console.error('Error toggling like:', error);
                this.$emit('show-notification', 'Помилка при зміні лайка.', 'error');
            }
        },
        async shareQuote() {
            const text = `"${this.quote.content}" — ${this.quote.user?.name || 'Користувач'}`;
            const url = `${window.location.origin}/books/${this.bookSlug}#quote-${this.quote.id}`;
            await shareContent({
                title: 'Цитата з книги',
                text: text,
                url: url
            });
        },
        startEdit() {
            this.isEditing = true;
            this.editContent = this.quote.content;
            this.editPageNumber = this.quote.page_number;
            this.editIsPublic = this.quote.is_public;
        },
        cancelEdit() {
            this.isEditing = false;
            this.editContent = '';
            this.editPageNumber = null;
            this.editIsPublic = true;
        },
        async saveEdit() {
            if (!this.editContent.trim()) {
                this.$emit('show-notification', 'Будь ласка, введіть текст цитати.', 'error');
                return;
            }

            this.isSaving = true;
            try {
                const response = await axios.put(`/books/${this.bookSlug}/quotes/${this.quote.id}`, {
                    content: this.editContent.trim(),
                    page_number: this.editPageNumber || null,
                    is_public: this.editIsPublic,
                });

                if (response.data.success) {
                    this.$emit('show-notification', 'Цитату оновлено!', 'success');
                    this.$emit('quote-updated', response.data.quote);
                    this.isEditing = false;
                } else {
                    this.$emit('show-notification', response.data.message || 'Помилка при оновленні цитати.', 'error');
                }
            } catch (error) {
                console.error('Error updating quote:', error);
                this.$emit('show-notification', error.response?.data?.message || 'Помилка при оновленні цитати.', 'error');
            } finally {
                this.isSaving = false;
            }
        },
        async deleteQuote() {
            const confirmed = await confirm('Ви впевнені, що хочете видалити цю цитату?', 'Підтвердження', 'warning');
            if (!confirmed) return;
            try {
                const response = await axios.delete(`/books/${this.effectiveBookSlug}/quotes/${this.quote.id}`);
                if (response.data.success) {
                    this.$emit('show-notification', 'Цитату видалено!', 'success');
                    this.$emit('quote-deleted', this.quote.id);
                } else {
                    this.$emit('show-notification', response.data.message || 'Помилка при видаленні цитати.', 'error');
                }
            } catch (error) {
                console.error('Error deleting quote:', error);
                this.$emit('show-notification', error.response?.data?.message || 'Помилка при видаленні цитати.', 'error');
            }
        },

        getContentPreview() {
            // Возвращаем превью контента для жалобы
            if (this.quote.content) {
                const text = this.quote.content;
                return text.substring(0, 100) + (text.length > 100 ? '...' : '');
            }
            return 'Цитата з книги';
        },

        getContentUrl() {
            // Возвращаем URL контента
            const bookSlug = this.effectiveBookSlug;
            return bookSlug ? `/books/${bookSlug}#quote-${this.quote.id}` : '#';
        },
        async toggleFavorite() {
            if (!this.quote.can_favorite || this.isFavoritePending) {
                return;
            }
            this.isFavoritePending = true;
            try {
                const bookSlug = this.effectiveBookSlug;
                if (!bookSlug) {
                    this.$emit('show-notification', 'Помилка: не вдалося визначити книгу.', 'error');
                    return;
                }
                const response = await axios.post(`/books/${bookSlug}/quotes/${this.quote.id}/favorite`);
                if (response.data.success) {
                    this.isFavorited = response.data.is_favorited;
                    this.$emit('show-notification', response.data.message, 'success');
                }
            } catch (error) {
                console.error('Error toggling favorite:', error);
                this.$emit('show-notification', error.response?.data?.message || 'Не вдалося змінити стан збереження.', 'error');
            } finally {
                this.isFavoritePending = false;
            }
        },
        profileUrl(username) {
            return username ? `/users/${username}` : '#';
        }
    }
};
</script>

<style scoped>
.quote-clamped-compact {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Quote card styles inherited from parent */

.notification-target {
    border-color: var(--color-action, #f97316);
    background-color: color-mix(in srgb, var(--color-action, #f97316) 8%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-action, #f97316) 24%, transparent);
}

@media (prefers-reduced-motion: reduce) {
    .notification-target {
        transition: none !important;
    }
}
</style>
